export type DeviceMassEditItem = {
  id: number
  transport?: string
  nat_method?: boolean
  jitter?: string
  time_zone?: string
  dst_enable?: boolean
  allow_provision_retrieval?: boolean
  server_group_host?: string
}

export interface ProvisioningDeviceListItem {
  id: number
  station_label: string
  device_model: number
  device_model_name?: string
  model_is_supported?: boolean
  device_mac_address: string
  last_provisioned_time?: string
  created_at: string
  warranty_expires_at?: string | null
  sidecar_enabled: boolean
  allow_provision_retrieval: boolean
  transport: string
  source?: string
  assigned_user?: { id: number; full_name: string }
  primary_extension?: { id: number; number: string; name: string }
  pbx_active?: boolean
  self_reported_model?: string
  self_reported_firmware_version?: string
  desired_firmware_version?: string
  last_provisioned_from?: string
  has_new_config?: boolean
}

export type DevicesListFilters = {
  manufacturer_id?: number
  device_model?: number
  firmware_version?: string
  warranty?: 'unknown' | 'in_warranty' | 'expired'
  transport?: string
  jitter?: string
  sidecar_enabled?: boolean
  /** Valid values: enabled | disabled | changes_pending */
  provisioning?: string
}

export interface ProvisioningDeviceDetail extends ProvisioningDeviceListItem {
  time_zone?: string
  dst_enable?: boolean
  corporate_directory?: number
  pages?: number
  line_button_count?: number
  jitter?: string
  server_group_host?: string
  nat_method?: boolean
  wifi_enabled?: boolean
  wifi_network_ssid?: string
  wifi_security_mode?: 'NONE' | 'WEP' | 'WPA' | 'WPA3'
  wifi_password?: string
  acoustic_shield_enabled?: boolean
  /** Buttons are returned inline with device detail and replaced atomically on update */
  buttons?: DeviceButtonItem[]
}

export interface CreateDevicePayload {
  device_model: number
  device_mac_address: string
  station_label: string
  name: string
  has_new_config: boolean
  assigned_user_id?: number
  primary_extension_id?: number
  corporate_directory?: number
  pages?: number
  line_button_count?: number
  source?: 'mango-new' | 'mango-refurbished' | 'customer'
  warranty_expires_at?: string | null
  time_zone?: string
  dst_enable?: boolean
  transport?: string
  jitter?: string
  server_group_host?: string
  allow_provision_retrieval?: boolean
  sidecar_enabled?: boolean
  nat_method?: boolean
  wifi_enabled?: boolean
  wifi_network_ssid?: string
  wifi_security_mode?: 'NONE' | 'WEP' | 'WPA' | 'WPA3'
  wifi_password?: string
  acoustic_shield_enabled?: boolean
  desired_firmware_version?: string
  buttons?: DeviceButtonPayload[]
}

export type UpdateDevicePayload = Partial<CreateDevicePayload>

export interface ValidateMacResponse {
  error: boolean
  message?: string
}

export interface DeviceModelItem {
  id: number
  name: string
  manufacturer_id: number
  manufacturer_name: string
  is_supported: boolean
  dss_key_count?: number
  buttons_per_page?: number
  pages?: number
  sidecar_supported?: boolean
  sidecar_button_count?: number
  supports_wifi?: boolean
  supports_acoustic_shield?: boolean
  supports_nat?: boolean
  image_url?: string
}

/**
 * Yealink AX83H — WiFi-only handset.
 *
 * mango-api#2011 enforces WiFi-required by `device_models.id === 49` (no DDL).
 * This client matches that id *and* the model name so local mocks / drifted PKs
 * still behave correctly — name matching is the established devices-ui
 * convention (see CORDLESS_BASE_MODEL_NAMES / COMMON_YEALINK_MODELS).
 */
export const AX83H_DEVICE_MODEL_ID = 49

const WIFI_REQUIRED_MODEL_NAMES = new Set(['AX83H'])

const normalizeModelNameForWifiRequired = (name: string) =>
  name.replace(/[\s-]+/g, '').toUpperCase()

export function isWifiRequiredDeviceModel(
  modelOrId:
    Pick<DeviceModelItem, 'id' | 'name'> | number | '' | null | undefined,
): boolean {
  if (modelOrId == null || modelOrId === '') return false
  if (typeof modelOrId === 'number') {
    return modelOrId === AX83H_DEVICE_MODEL_ID
  }
  if (modelOrId.id === AX83H_DEVICE_MODEL_ID) return true
  if (!modelOrId.name) return false
  return WIFI_REQUIRED_MODEL_NAMES.has(
    normalizeModelNameForWifiRequired(modelOrId.name),
  )
}

export interface SystemDefaultsItem {
  default_time_zone?: string
  preferred_codec?: string
  pbx_id?: number
}

/** Returned inline with device detail; replaced atomically on update */
export interface DeviceButtonItem {
  id: number
  type: 'LineButton' | 'SidecarButton'
  display_count: number
  button_function: string
  button_value?: string
  button_label?: string
  /** API may return a nested object `{id, number, name}` or a plain numeric ID */
  extension?: number | { id: number; number: string; name: string }
  speed_dial_value?: string
  slot_number?: number
}

/** Sent inline with device create/update; no id or device FK needed */
export interface DeviceButtonPayload {
  type: 'LineButton' | 'SidecarButton'
  display_count: number
  button_function?: string
  button_value?: string
  button_label?: string
  extension?: number
  speed_dial_value?: string
  slot_number?: number
}

export interface ProvisioningUserItem {
  id: number
  full_name: string
  username?: string
}

export interface ProvisioningDirectoryItem {
  id: number
  name: string
}

export type DeviceSortColumn =
  | 'station_label'
  | 'primary_extension'
  | 'assigned_user'
  | 'device_model'
  | 'device_mac_address'
  | 'last_provisioned_time'
  | 'created_at'

export type SortDirection = 'asc' | 'desc'

export interface DeviceSortState {
  column: DeviceSortColumn | null
  direction: SortDirection
}
